import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { CheckCircle2, ArrowLeft, ArrowRight, Send, Loader2, Info, ExternalLink } from 'lucide-react';
import { getT, DEFAULT_LANG } from '@/lib/intakeWizardI18n';
import StepShell from './IntakeWizard/StepShell';
import ProgressBar from './IntakeWizard/ProgressBar';
import AIFeedbackPanel from './IntakeWizard/AIFeedbackPanel';
import LanguageToggle from './IntakeWizard/LanguageToggle';

const TOTAL_STEPS = 7;
const DRAFT_KEY = 'speedforce_use_case_draft_v1';
const LANG_KEY = 'speedforce_use_case_lang';

function emptyState() {
  return {
    session_id: cryptoRandom(),
    problem_statement: '',
    problem_refined: '',
    users: { description: '', count: 0, frequency: 'weekly', hours_impacted_weekly: 0 },
    current_process: { description: '', minutes_per_task: 0, volume_per_week: 0, hours_per_year: 0 },
    success_metrics: { description: '', kpis: [] },
    data: { types: [], description: '', sensitivity_tier: '', gdpr_flags: [], ai_act_flags: [] },
    alternatives_considered: '',
    ai_suggested_alternatives: [],
    ai_summary: '',
    impact_score: 0,
    complexity_score: 0,
    priority_tier: 'standard',
  };
}

function cryptoRandom() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `uc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function computeHoursPerYear(minutes, volume) {
  const m = Number(minutes) || 0;
  const v = Number(volume) || 0;
  return Math.round((m / 60) * v * 52);
}

function computeWeeklyHoursImpacted(count, frequency) {
  const c = Number(count) || 0;
  const freqHours = { daily: 5, weekly: 1, monthly: 0.25 }[frequency] || 1;
  return Math.round(c * freqHours * 0.3);
}

function isStepComplete(step, state, t) {
  switch (step) {
    case 1:
      return (state.problem_statement || '').trim().length >= t.steps[1].minChars;
    case 2:
      return !!state.users.description.trim() && Number(state.users.count) > 0 && !!state.users.frequency;
    case 3:
      return !!state.current_process.description.trim()
        && Number(state.current_process.minutes_per_task) > 0
        && Number(state.current_process.volume_per_week) > 0;
    case 4:
      return !!state.success_metrics.description.trim() && state.success_metrics.kpis.length > 0;
    case 5:
      return state.data.types.length > 0 && !!state.data.description.trim();
    case 6:
      return true; // optional
    case 7:
      return !!state.ai_summary;
    default:
      return false;
  }
}

export default function IntakeWizard() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || DEFAULT_LANG);
  const t = useMemo(() => getT(lang), [lang]);

  const [step, setStep] = useState(1);
  const [state, setState] = useState(emptyState);
  const [aiFeedback, setAiFeedback] = useState({});
  const [refiningStep, setRefiningStep] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftChecked, setDraftChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [rateLimitMsg, setRateLimitMsg] = useState('');

  // Load draft on mount. Sets draftChecked=true once done so the auto-save
  // effect below knows it's safe to start writing.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.state) setHasDraft(true);
      }
    } catch { /* ignore */ }
    setDraftChecked(true);
  }, []);

  // Persist language
  useEffect(() => { localStorage.setItem(LANG_KEY, lang); }, [lang]);

  // Auto-save draft on change. Skip until the initial draft check completes,
  // and while a draft banner is showing (so we don't clobber the saved draft
  // before the user has decided to resume or discard it).
  useEffect(() => {
    if (!draftChecked || hasDraft || submitResult) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, state, aiFeedback }));
    } catch { /* ignore */ }
  }, [step, state, aiFeedback, submitResult, draftChecked, hasDraft]);

  // Keep derived fields in sync
  useEffect(() => {
    const hoursPerYear = computeHoursPerYear(state.current_process.minutes_per_task, state.current_process.volume_per_week);
    if (hoursPerYear !== state.current_process.hours_per_year) {
      setState((s) => ({ ...s, current_process: { ...s.current_process, hours_per_year: hoursPerYear } }));
    }
  }, [state.current_process.minutes_per_task, state.current_process.volume_per_week, state.current_process.hours_per_year]);

  useEffect(() => {
    const weekly = computeWeeklyHoursImpacted(state.users.count, state.users.frequency);
    if (weekly !== state.users.hours_impacted_weekly) {
      setState((s) => ({ ...s, users: { ...s.users, hours_impacted_weekly: weekly } }));
    }
  }, [state.users.count, state.users.frequency, state.users.hours_impacted_weekly]);

  const resumeDraft = useCallback(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.state) {
          setState(parsed.state);
          setAiFeedback(parsed.aiFeedback || {});
          setStep(parsed.step || 1);
        }
      }
    } catch { /* ignore */ }
    setHasDraft(false);
  }, []);

  const discardDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
    setState(emptyState());
    setAiFeedback({});
    setStep(1);
    setHasDraft(false);
  }, []);

  const buildStepInput = useCallback((s) => {
    switch (s) {
      case 1: return state.problem_statement;
      case 2: return JSON.stringify(state.users);
      case 3: return JSON.stringify(state.current_process);
      case 4: return JSON.stringify(state.success_metrics);
      case 5: return JSON.stringify(state.data);
      case 6: return state.alternatives_considered || '(none yet)';
      case 7: return '';
      default: return '';
    }
  }, [state]);

  const refineStep = useCallback(async (s) => {
    setRateLimitMsg('');
    setRefiningStep(s);
    try {
      const res = await api.post('/ai/intake/refine', {
        session_id: state.session_id,
        step: s,
        lang,
        context: {
          problem_statement: state.problem_statement,
          problem_refined: state.problem_refined,
          users: state.users,
          current_process: state.current_process,
          success_metrics: state.success_metrics,
          data: state.data,
          alternatives_considered: state.alternatives_considered,
        },
        user_input: buildStepInput(s),
      });
      const data = res.data || {};
      setAiFeedback((fb) => ({ ...fb, [s]: data }));

      // Apply step-specific side effects
      if (s === 5 && data.refined) {
        const tierMatch = data.refined.match(/T[1-4]/i);
        if (tierMatch) {
          const tier = tierMatch[0].toUpperCase();
          const flags = Array.isArray(data.flags) ? data.flags : [];
          setState((prev) => ({
            ...prev,
            data: {
              ...prev.data,
              sensitivity_tier: tier,
              gdpr_flags: flags.filter((f) => String(f).includes('gdpr')),
              ai_act_flags: flags.filter((f) => String(f).includes('ai_act')),
            },
          }));
        }
      }
      if (s === 6 && data.refined) {
        const lines = data.refined.split(/\n/).map((l) => l.trim()).filter(Boolean).slice(0, 5);
        setState((prev) => ({ ...prev, ai_suggested_alternatives: lines }));
      }
      if (s === 7) {
        setState((prev) => ({
          ...prev,
          ai_summary: data.ai_summary || '',
          impact_score: Number(data.impact_score) || 0,
          complexity_score: Number(data.complexity_score) || 0,
          priority_tier: data.priority_tier || 'standard',
        }));
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        setRateLimitMsg(err.response.data?.detail || 'AI rate limit reached.');
      } else {
        setAiFeedback((fb) => ({ ...fb, [s]: { refined: '', suggestion: '', flags: [] } }));
      }
    } finally {
      setRefiningStep(null);
    }
  }, [state, lang, buildStepInput]);

  const canProceed = isStepComplete(step, state, t);

  const goNext = useCallback(() => {
    if (!canProceed) return;
    if (step < TOTAL_STEPS) setStep(step + 1);
  }, [step, canProceed]);

  const goBack = useCallback(() => { if (step > 1) setStep(step - 1); }, [step]);

  const acceptRefinedForStep1 = useCallback((refined) => {
    setState((s) => ({ ...s, problem_statement: refined, problem_refined: refined }));
  }, []);

  const submit = useCallback(async () => {
    setSubmitting(true);
    try {
      const payload = {
        request_type: 'ai_use_case',
        lang,
        problem_statement: state.problem_statement,
        problem_refined: state.problem_refined || state.problem_statement,
        users: state.users,
        current_process: state.current_process,
        success_metrics: state.success_metrics,
        data: state.data,
        alternatives_considered: state.alternatives_considered,
        ai_suggested_alternatives: state.ai_suggested_alternatives,
        ai_summary: state.ai_summary,
        impact_score: state.impact_score,
        complexity_score: state.complexity_score,
        priority_tier: state.priority_tier,
      };
      const res = await api.post('/ai/intake/submit', {
        session_id: state.session_id,
        lang,
        payload,
      });
      setSubmitResult(res.data);
      localStorage.removeItem(DRAFT_KEY);
    } catch (err) {
      setRateLimitMsg(err?.response?.data?.detail || 'Submission failed — please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [state, lang]);

  if (submitResult) {
    return <Confirmation result={submitResult} t={t} onStartNew={() => {
      setSubmitResult(null);
      discardDraft();
      navigate('/intake/use-case');
    }} />;
  }

  const stepNames = t.steps.slice(1).map((s) => s.name);

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-[#30302F] dark:text-white">{t.title}</h1>
          <p className="text-sm text-[#30302F]/70 dark:text-white/70 mt-1">{t.subtitle}</p>
        </div>
        <LanguageToggle lang={lang} onChange={setLang} />
      </div>

      {/* Resume-draft banner */}
      {hasDraft && (
        <div className="mt-4 flex items-center justify-between gap-3 p-3 rounded-lg bg-[#f0dcf0] dark:bg-[#98139C]/10 border border-[#98139C]/30">
          <div className="flex items-center gap-2 text-sm text-[#4F0077] dark:text-[#d87bdb]">
            <Info size={14} /> {t.continueDraft}
          </div>
          <div className="flex gap-2">
            <button
              onClick={resumeDraft}
              className="text-xs font-semibold uppercase text-[#98139C] dark:text-[#d87bdb] hover:underline"
            >
              {t.continueDraft}
            </button>
            <button
              onClick={discardDraft}
              className="text-xs font-semibold uppercase text-[#30302F]/60 dark:text-white/50 hover:text-[#30302F] dark:hover:text-white"
            >
              {t.discardDraft}
            </button>
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="mt-6 mb-6">
        <ProgressBar step={step} total={TOTAL_STEPS} stepNames={stepNames} />
        <p className="mt-2 text-xs text-[#30302F]/60 dark:text-white/50">
          {t.progressStep(step, TOTAL_STEPS)}
        </p>
      </div>

      {/* Rate limit notice */}
      {rateLimitMsg && (
        <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-900 dark:text-amber-200">
          {rateLimitMsg}
        </div>
      )}

      {/* Step body */}
      <div className="bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 rounded-xl p-6">
        {step === 1 && (
          <Step1 state={state} setState={setState} t={t} step={1}
                 feedback={aiFeedback[1]} onRefine={() => refineStep(1)}
                 refining={refiningStep === 1} onAccept={acceptRefinedForStep1} />
        )}
        {step === 2 && (
          <Step2 state={state} setState={setState} t={t} step={2}
                 feedback={aiFeedback[2]} onRefine={() => refineStep(2)}
                 refining={refiningStep === 2} />
        )}
        {step === 3 && (
          <Step3 state={state} setState={setState} t={t} step={3}
                 feedback={aiFeedback[3]} onRefine={() => refineStep(3)}
                 refining={refiningStep === 3} />
        )}
        {step === 4 && (
          <Step4 state={state} setState={setState} t={t} step={4}
                 feedback={aiFeedback[4]} onRefine={() => refineStep(4)}
                 refining={refiningStep === 4} />
        )}
        {step === 5 && (
          <Step5 state={state} setState={setState} t={t} step={5}
                 feedback={aiFeedback[5]} onRefine={() => refineStep(5)}
                 refining={refiningStep === 5} />
        )}
        {step === 6 && (
          <Step6 state={state} setState={setState} t={t} step={6}
                 feedback={aiFeedback[6]} onRefine={() => refineStep(6)}
                 refining={refiningStep === 6} />
        )}
        {step === 7 && (
          <Step7 state={state} t={t} onGenerate={() => refineStep(7)}
                 refining={refiningStep === 7} />
        )}
      </div>

      {/* Footer nav */}
      <div className="flex items-center justify-between mt-6">
        <button
          type="button"
          onClick={goBack}
          disabled={step === 1}
          className="inline-flex items-center gap-1.5 px-4 h-10 text-sm font-medium text-[#30302F] dark:text-white hover:bg-[#faf8fb] dark:hover:bg-white/5 rounded-full disabled:opacity-40"
        >
          <ArrowLeft size={14} /> {t.back}
        </button>

        {step < TOTAL_STEPS ? (
          <button
            type="button"
            onClick={goNext}
            disabled={!canProceed}
            className="inline-flex items-center gap-1.5 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-6 h-10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t.next} <ArrowRight size={14} />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={!canProceed || submitting}
            className="inline-flex items-center gap-1.5 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-6 h-10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {submitting ? t.submitting : t.submit}
          </button>
        )}
      </div>
    </div>
  );
}

// -------------------------------- Individual steps --------------------------------

function inputBase() {
  return 'w-full px-3 py-2 text-sm rounded-md border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-[#1a1a2e] text-[#30302F] dark:text-white focus:outline-none focus:border-[#98139C] dark:focus:border-[#d87bdb] placeholder:text-[#30302F]/40 dark:placeholder:text-white/30';
}

function labelBase() {
  return 'text-xs font-semibold uppercase tracking-wide text-[#30302F]/70 dark:text-white/70 mb-1.5 block';
}

function panelLabels(t) {
  return { refine: t.refine, refining: t.refining, advisory: t.aiAdvisory };
}

function Step1({ state, setState, t, step, feedback, onRefine, refining, onAccept }) {
  const cfg = t.steps[1];
  const remaining = Math.max(0, cfg.minChars - (state.problem_statement || '').trim().length);
  return (
    <StepShell heading={cfg.heading}>
      <textarea
        className={`${inputBase()} min-h-[140px] resize-y`}
        value={state.problem_statement}
        onChange={(e) => setState((s) => ({
          ...s,
          problem_statement: e.target.value,
          problem_refined: e.target.value === s.problem_refined ? s.problem_refined : '',
        }))}
        placeholder={cfg.placeholder}
      />
      <p className="mt-1 text-xs text-[#30302F]/60 dark:text-white/50">
        {remaining > 0 ? `${remaining} chars to go` : '✓'}
      </p>
      <AIFeedbackPanel
        refining={refining}
        feedback={feedback}
        onRefine={onRefine}
        disabled={!(state.problem_statement || '').trim()}
        labels={panelLabels(t)}
        onAccept={onAccept}
      />
    </StepShell>
  );
}

function Step2({ state, setState, t, step, feedback, onRefine, refining }) {
  const cfg = t.steps[2];
  const u = state.users;
  return (
    <StepShell heading={cfg.heading} description={cfg.description}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className={labelBase()}>{cfg.fields.description}</label>
          <input
            className={inputBase()}
            value={u.description}
            onChange={(e) => setState((s) => ({ ...s, users: { ...s.users, description: e.target.value } }))}
          />
        </div>
        <div>
          <label className={labelBase()}>{cfg.fields.count}</label>
          <input
            type="number" min="0"
            className={inputBase()}
            value={u.count || ''}
            onChange={(e) => setState((s) => ({ ...s, users: { ...s.users, count: Number(e.target.value) || 0 } }))}
          />
        </div>
        <div>
          <label className={labelBase()}>{cfg.fields.frequency}</label>
          <select
            className={inputBase()}
            value={u.frequency}
            onChange={(e) => setState((s) => ({ ...s, users: { ...s.users, frequency: e.target.value } }))}
          >
            {Object.entries(cfg.fields.frequencyOptions).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>
      <AIFeedbackPanel
        refining={refining} feedback={feedback} onRefine={onRefine}
        disabled={!u.description.trim() || !u.count}
        labels={panelLabels(t)}
      />
    </StepShell>
  );
}

function Step3({ state, setState, t, step, feedback, onRefine, refining }) {
  const cfg = t.steps[3];
  const cp = state.current_process;
  return (
    <StepShell heading={cfg.heading}>
      <div className="space-y-4">
        <div>
          <label className={labelBase()}>{cfg.fields.description}</label>
          <textarea
            className={`${inputBase()} min-h-[96px] resize-y`}
            value={cp.description}
            onChange={(e) => setState((s) => ({ ...s, current_process: { ...s.current_process, description: e.target.value } }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelBase()}>{cfg.fields.minutes_per_task}</label>
            <input
              type="number" min="0"
              className={inputBase()}
              value={cp.minutes_per_task || ''}
              onChange={(e) => setState((s) => ({ ...s, current_process: { ...s.current_process, minutes_per_task: Number(e.target.value) || 0 } }))}
            />
          </div>
          <div>
            <label className={labelBase()}>{cfg.fields.volume_per_week}</label>
            <input
              type="number" min="0"
              className={inputBase()}
              value={cp.volume_per_week || ''}
              onChange={(e) => setState((s) => ({ ...s, current_process: { ...s.current_process, volume_per_week: Number(e.target.value) || 0 } }))}
            />
          </div>
        </div>
        <div className="rounded-md bg-[#faf8fb] dark:bg-white/[0.03] border border-[#e3e3e3] dark:border-white/10 px-4 py-3 flex items-baseline justify-between">
          <span className="text-xs uppercase tracking-wide text-[#30302F]/70 dark:text-white/70">{cfg.computedLabel}</span>
          <span className="text-2xl font-bold text-[#98139C] dark:text-[#d87bdb]">
            {cp.hours_per_year} <span className="text-sm font-normal">{cfg.computedUnit}</span>
          </span>
        </div>
      </div>
      <AIFeedbackPanel
        refining={refining} feedback={feedback} onRefine={onRefine}
        disabled={!cp.description.trim()}
        labels={panelLabels(t)}
      />
    </StepShell>
  );
}

function Step4({ state, setState, t, step, feedback, onRefine, refining }) {
  const cfg = t.steps[4];
  const sm = state.success_metrics;
  const toggleKpi = (k) => setState((s) => {
    const kpis = s.success_metrics.kpis.includes(k)
      ? s.success_metrics.kpis.filter((x) => x !== k)
      : [...s.success_metrics.kpis, k];
    return { ...s, success_metrics: { ...s.success_metrics, kpis } };
  });
  return (
    <StepShell heading={cfg.heading}>
      <textarea
        className={`${inputBase()} min-h-[96px] resize-y`}
        value={sm.description}
        onChange={(e) => setState((s) => ({ ...s, success_metrics: { ...s.success_metrics, description: e.target.value } }))}
        placeholder={cfg.placeholder}
      />
      <div className="mt-4">
        <p className={labelBase()}>{cfg.kpiHeading}</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(cfg.kpiOptions).map(([k, v]) => {
            const active = sm.kpis.includes(k);
            return (
              <button
                type="button" key={k} onClick={() => toggleKpi(k)}
                aria-pressed={active}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                  active
                    ? 'bg-[#98139C] text-white border-[#98139C]'
                    : 'bg-white dark:bg-transparent text-[#30302F] dark:text-white border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C]'
                }`}
              >
                {v}
              </button>
            );
          })}
        </div>
      </div>
      <AIFeedbackPanel
        refining={refining} feedback={feedback} onRefine={onRefine}
        disabled={!sm.description.trim()}
        labels={panelLabels(t)}
      />
    </StepShell>
  );
}

function Step5({ state, setState, t, step, feedback, onRefine, refining }) {
  const cfg = t.steps[5];
  const d = state.data;
  const toggleType = (k) => setState((s) => {
    const types = s.data.types.includes(k)
      ? s.data.types.filter((x) => x !== k)
      : [...s.data.types, k];
    return { ...s, data: { ...s.data, types } };
  });
  return (
    <StepShell heading={cfg.heading}>
      <p className={labelBase()}>{cfg.typesHeading}</p>
      <div className="flex flex-wrap gap-2">
        {Object.entries(cfg.typeOptions).map(([k, v]) => {
          const active = d.types.includes(k);
          return (
            <button
              type="button" key={k} onClick={() => toggleType(k)}
              aria-pressed={active}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                active
                  ? 'bg-[#98139C] text-white border-[#98139C]'
                  : 'bg-white dark:bg-transparent text-[#30302F] dark:text-white border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C]'
              }`}
            >
              {v}
            </button>
          );
        })}
      </div>
      <div className="mt-4">
        <textarea
          className={`${inputBase()} min-h-[96px] resize-y`}
          value={d.description}
          onChange={(e) => setState((s) => ({ ...s, data: { ...s.data, description: e.target.value } }))}
          placeholder={cfg.descPlaceholder}
        />
      </div>
      {d.sensitivity_tier && (
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] text-xs font-semibold">
          {cfg.sensitivityLabel}: {d.sensitivity_tier}
        </div>
      )}
      <AIFeedbackPanel
        refining={refining} feedback={feedback} onRefine={onRefine}
        disabled={d.types.length === 0 || !d.description.trim()}
        labels={panelLabels(t)}
      />
    </StepShell>
  );
}

function Step6({ state, setState, t, step, feedback, onRefine, refining }) {
  const cfg = t.steps[6];
  return (
    <StepShell heading={cfg.heading}>
      <textarea
        className={`${inputBase()} min-h-[120px] resize-y`}
        value={state.alternatives_considered}
        onChange={(e) => setState((s) => ({ ...s, alternatives_considered: e.target.value }))}
        placeholder={cfg.placeholder}
      />
      {state.ai_suggested_alternatives.length > 0 && (
        <div className="mt-4 border border-[#e3e3e3] dark:border-white/10 rounded-lg p-4 bg-[#faf8fb] dark:bg-white/[0.03]">
          <p className={labelBase()}>{cfg.aiSuggestLabel}</p>
          <ul className="space-y-1 text-sm text-[#30302F] dark:text-white">
            {state.ai_suggested_alternatives.map((alt, i) => (
              <li key={i} className="pl-2">• {alt.replace(/^[-•]\s*/, '')}</li>
            ))}
          </ul>
        </div>
      )}
      <AIFeedbackPanel
        refining={refining} feedback={feedback} onRefine={onRefine}
        disabled={false}
        labels={panelLabels(t)}
      />
    </StepShell>
  );
}

function Step7({ state, t, onGenerate, refining }) {
  const cfg = t.steps[7];
  const priorityColor = {
    fast_track: 'text-emerald-600 dark:text-emerald-400',
    standard: 'text-amber-600 dark:text-amber-400',
    needs_refinement: 'text-red-600 dark:text-red-400',
  }[state.priority_tier] || '';

  return (
    <StepShell heading={cfg.heading}>
      {/* Summary read-only view */}
      <dl className="divide-y divide-[#e3e3e3] dark:divide-white/10 text-sm">
        <ReviewRow label="Problem" value={state.problem_refined || state.problem_statement} />
        <ReviewRow label="Users" value={`${state.users.description} — ~${state.users.count} ${state.users.frequency}`} />
        <ReviewRow label="Current process" value={`${state.current_process.description} • ${state.current_process.hours_per_year} hours/year`} />
        <ReviewRow label="Success" value={`${state.success_metrics.description} (${state.success_metrics.kpis.join(', ')})`} />
        <ReviewRow label="Data" value={`${state.data.types.join(', ')} — ${state.data.sensitivity_tier || 'tier not assigned'}`} />
        {state.alternatives_considered && (
          <ReviewRow label="Alternatives" value={state.alternatives_considered} />
        )}
      </dl>

      <div className="mt-6">
        <button
          type="button"
          onClick={onGenerate}
          disabled={refining}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#98139C] dark:text-[#d87bdb] hover:underline disabled:opacity-50"
        >
          {refining ? <Loader2 size={14} className="animate-spin" /> : null}
          {refining ? cfg.generating : cfg.generateButton}
        </button>
      </div>

      {state.ai_summary && (
        <div className="mt-4 p-4 rounded-lg bg-[#faf8fb] dark:bg-white/[0.03] border border-[#e3e3e3] dark:border-white/10 space-y-3">
          <div className="grid grid-cols-3 gap-4">
            <Stat label={cfg.impactLabel} value={state.impact_score} />
            <Stat label={cfg.complexityLabel} value={state.complexity_score} />
            <div>
              <p className={labelBase()}>{cfg.priorityLabel}</p>
              <p className={`text-base font-bold ${priorityColor}`}>
                {t.priorityLabel[state.priority_tier] || state.priority_tier}
              </p>
            </div>
          </div>
          <div className="pt-3 border-t border-[#e3e3e3] dark:border-white/10">
            <p className={labelBase()}>{cfg.summaryLabel}</p>
            <p className="text-sm text-[#30302F] dark:text-white leading-relaxed">{state.ai_summary}</p>
          </div>
        </div>
      )}
    </StepShell>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-3">
      <dt className="text-xs uppercase tracking-wide text-[#30302F]/60 dark:text-white/50">{label}</dt>
      <dd className="text-[#30302F] dark:text-white whitespace-pre-wrap">{value || '—'}</dd>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-[#30302F]/70 dark:text-white/70 mb-1.5">{label}</p>
      <p className="text-2xl font-bold text-[#98139C] dark:text-[#d87bdb]">{value || '—'}</p>
    </div>
  );
}

function Confirmation({ result, t, onStartNew }) {
  const priorityColor = {
    fast_track: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    standard: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    needs_refinement: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  }[result.priority_tier] || 'bg-gray-100 text-gray-800';

  return (
    <div className="max-w-2xl mx-auto px-6 py-16">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mb-4">
          <CheckCircle2 size={28} className="text-emerald-600 dark:text-emerald-400" />
        </div>
        <h1 className="text-2xl font-bold text-[#30302F] dark:text-white">{t.confirmationTitle}</h1>
      </div>

      <div className="mt-8 bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-[#30302F]/70 dark:text-white/70">
            {t.confirmationPriority}
          </span>
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${priorityColor}`}>
            {t.priorityLabel[result.priority_tier] || result.priority_tier}
          </span>
        </div>
        {result.ai_summary && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#30302F]/70 dark:text-white/70 mb-2">
              {t.confirmationSummary}
            </p>
            <p className="text-sm text-[#30302F] dark:text-white leading-relaxed">
              {result.ai_summary}
            </p>
          </div>
        )}
        <div className="pt-4 border-t border-[#e3e3e3] dark:border-white/10">
          <a
            href={result.link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#98139C] dark:text-[#d87bdb] hover:underline"
          >
            {t.confirmationLink} <ExternalLink size={14} />
          </a>
        </div>
      </div>

      <div className="mt-8 flex justify-center">
        <button
          onClick={onStartNew}
          className="text-sm font-semibold text-[#30302F]/70 dark:text-white/70 hover:text-[#98139C] dark:hover:text-[#d87bdb]"
        >
          + New request
        </button>
      </div>
    </div>
  );
}
